'use client'
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useFilter } from '@/app/context/FilterContext'

// Slot-machine: each character animates in/out vertically
const containerVariants = {
  animate: { transition: { staggerChildren: 0.025 } },
  exit: { transition: { staggerChildren: 0.015 } },
};

const charVariants = {
  initial: { y: 18, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.18, ease: 'easeOut' as const } },
  exit: { y: -18, opacity: 0, transition: { duration: 0.12, ease: 'easeIn' as const } },
};

const LogoButton = ({ className = '' }: { className?: string }) => {
  const { activeFilter, setActiveFilter, filters } = useFilter();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const displayText = activeFilter ?? 'cody-taylor.com';

  function handleClick() {
    if (activeFilter) {
      setActiveFilter(null);
      setDropdownOpen(false);
    } else {
      setDropdownOpen((o) => !o);
    }
  }

  // Click-outside closes dropdown
  useEffect(() => {
    if (!dropdownOpen) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [dropdownOpen]);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <motion.div
        className={'logobutton ' + className}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        onClick={handleClick}
        style={{ display: 'inline-flex', overflow: 'hidden', cursor: 'pointer' }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={displayText}
            variants={containerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ display: 'inline-flex' }}
          >
            {displayText.split('').map((char, i) => (
              <motion.span key={i} variants={charVariants} style={{ display: 'inline-block' }}>
                {char === ' ' ? '\u00a0' : char}
              </motion.span>
            ))}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Filter dropdown (shows when no filter active and logo clicked) */}
      <AnimatePresence>
        {dropdownOpen && filters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 6,
              background: 'var(--background2)',
              border: '1px solid var(--uforeground2)',
              borderRadius: 12,
              padding: '6px 4px',
              minWidth: 180,
              zIndex: 200,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {filters.map((name) => (
              <button
                key={name}
                onClick={() => { setActiveFilter(name); setDropdownOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  color: 'var(--foreground)',
                  borderRadius: 8,
                  fontSize: '0.95em',
                  fontFamily: 'inherit',
                }}
              >
                {name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LogoButton;
